import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Lock, WarningCircle, User } from 'phosphor-react';
import GeniLoader from './GeniLoader';

/**
 * VanityUrlResolver
 * 
 * Handles /u/{username} routes by:
 * 1. Looking up the username via backend API
 * 2. Redirecting to /profile/{userId}/dashboard if found and public
 * 3. Showing appropriate error for 403 (not public) or 404 (not found)
 * 
 * This keeps the public profile architecture simple by reusing
 * the existing /profile/{userId} system.
 */
export default function VanityUrlResolver() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'not_found' | 'not_public'

  useEffect(() => {
    const resolveUsername = async () => {
      if (!username) {
        setError('No username provided');
        setErrorType('not_found');
        setLoading(false);
        return;
      }

      try {
        // Call backend to resolve username to userId
        const response = await publicApi.get(`/public/profile/by-username/${encodeURIComponent(username)}`);
        const { redirect_to } = response.data;
        
        // Redirect to the userId-based route
        navigate(redirect_to, { replace: true });
        
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        
        if (status === 404) {
          setError(`User "${username}" not found`);
          setErrorType('not_found');
        } else if (status === 403) {
          setError(`This profile is not public`);
          setErrorType('not_public');
        } else {
          setError(detail || 'Failed to load profile');
          setErrorType('not_found');
        }
        setLoading(false);
      }
    };

    resolveUsername();
  }, [username, navigate]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <GeniLoader size="lg" />
          <p className="text-white/60 mt-4">Looking up @{username}...</p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {/* Icon based on error type */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
          {errorType === 'not_public' ? (
            <Lock size={40} className="text-amber-400" />
          ) : (
            <User size={40} className="text-white/40" />
          )}
        </div>
        
        {/* Error message */}
        <h2 className="text-2xl font-bold text-white mb-3">
          {errorType === 'not_public' ? 'Profile Not Available' : 'User Not Found'}
        </h2>
        <p className="text-white/60 mb-6">{error}</p>
        
        {/* Action button */}
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-white text-black font-bold rounded-sm hover:bg-gray-200 transition-colors"
          data-testid="vanity-error-home-btn"
        >
          Go to Homepage
        </button>
        
        {/* Hint for not_public */}
        {errorType === 'not_public' && (
          <p className="text-white/40 text-sm mt-6">
            The user @{username} has not enabled their public profile.
          </p>
        )}
      </div>
    </div>
  );
}
